import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import { warehouseService } from '@/services/warehouseService'

export type SalesQuotationOption = { id: number; name: string }

export type SalesQuotationTaxRate = {
  id: number
  nameEn: string
  nameAr: string
  percent: number
  isDefault: boolean
}

/** Row from GetAllWarehouseItems (stock by warehouse / item / batch). */
export type SalesQuotationWarehouseStockRow = {
  id: number
  itemId: number
  batchItemId: number
  warehouseId: number
  availableQuantity: number
}

/** Normalized from GetItemDetails (field names vary by API). */
export type SalesQuotationItemDetails = {
  availableQuantity: number
  unitPrice: number
}

/** One line inside `SalesQuotationLinesJson` (multipart). `tax` = tax rate id. */
export type SalesQuotationSaveLine = {
  taxAmount: number
  discountPercent: number
  unitPrice: number
  orderId: number
  quantity: number
  tax: number
  lineAmount: number
  itemId: number
  /** Wire format matches SalesOrder lines: JSON uses strings `'true'` / `'false'` (not booleans). */
  isDeleted: boolean
  batchId: number
  unitCostPrice: number
  discountAmount: number
  taxPercent: number
  id: number
  taxableAmount: number
}

export type SalesQuotationSavePayload = {
  id: number
  quotCode: string
  code: string
  customerName: string
  warehouseName: string
  reference: string
  orderDate: string
  estDeliveryDate: string
  customerId: number
  paymentMethod: number
  taxableAmount: number
  taxAmount: number
  discount: number
  total: number
  salesPersonId: number
  warehouseId: number
  paymentTerm: number
  orderStatus: number
  attachmentPath: string
  createdDate: string
  createdBy: number
  modifiedDate: string
  modifiedBy: number
  branchId: number
  attachmentFile?: File | null
  itemList: SalesQuotationSaveLine[]
}

function unwrap(payload: unknown) {
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>
    if ('success' in o && 'data' in o) return o.data
    if ('data' in o) return o.data
  }
  return payload
}

function toArray(payload: unknown): unknown[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown[] }).data)) {
    return (raw as { data: unknown[] }).data
  }
  if (raw && typeof raw === 'object') return [raw]
  return []
}

/** Resolves nested API shapes like `{ data: { items: [] } }` or `{ result: [] }`. */
function extractListPayload(payload: unknown): unknown[] {
  let cur: unknown = unwrap(payload)
  for (let depth = 0; depth < 6; depth++) {
    if (Array.isArray(cur)) return cur
    if (!cur || typeof cur !== 'object') break
    const o = cur as Record<string, unknown>
    let next: unknown
    for (const key of [
      'data',
      'Data',
      'items',
      'Items',
      'result',
      'Result',
      'records',
      'rows',
      'Rows',
      'list',
      'value',
      'Value',
    ]) {
      const v = o[key]
      if (Array.isArray(v)) return v
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        next = v
        break
      }
    }
    if (next === undefined) break
    cur = next
  }
  if (cur && typeof cur === 'object' && !Array.isArray(cur)) return [cur]
  return []
}

/** Header from GetSalesQuotationById — normalized for the edit form. */
export type SalesQuotationDetailMaster = {
  id: number
  quotCode: string
  code: string
  reference: string
  orderDate: string
  estDeliveryDate: string
  customerId: number
  customerName: string
  warehouseId: number
  warehouseName: string
  paymentMethod: number
  paymentTerm: number
  orderStatus: number
  attachmentPath: string
  createdDate: string
  createdBy: number
  branchId: number
}

/** One line from GetSalesQuotationLinesById. */
export type SalesQuotationDetailLine = {
  id: number
  itemId: number
  batchId: number
  quantity: number
  unitPrice: number
  discountPercent: number
  /** Tax rate id (same as save payload `tax`). */
  taxRateId: number
  isDeleted: boolean
}

function detailDateOnly(v: unknown): string {
  const s = String(v ?? '')
  if (!s) return ''
  return s.includes('T') ? s.split('T')[0] : s.slice(0, 10)
}

function mapSalesQuotationDetailMaster(row: Record<string, unknown>): SalesQuotationDetailMaster {
  return {
    id: Number(row.id ?? row.Id ?? 0),
    quotCode: String(row.quotCode ?? row.QuotCode ?? ''),
    code: String(row.code ?? row.Code ?? ''),
    reference: String(row.reference ?? row.Reference ?? ''),
    orderDate: detailDateOnly(row.orderDate ?? row.OrderDate),
    estDeliveryDate: detailDateOnly(row.estDeliveryDate ?? row.EstDeliveryDate),
    customerId: Number(row.customerId ?? row.CustomerId ?? 0),
    customerName: String(row.customerName ?? row.CustomerName ?? ''),
    warehouseId: Number(row.warehouseId ?? row.WarehouseId ?? 0),
    warehouseName: String(row.warehouseName ?? row.WarehouseName ?? ''),
    paymentMethod: Number(row.paymentMethod ?? row.PaymentMethod ?? 0),
    paymentTerm: Number(row.paymentTerm ?? row.PaymentTerm ?? 0),
    orderStatus: Number(row.orderStatus ?? row.OrderStatus ?? 0),
    attachmentPath: String(row.attachmentPath ?? row.AttachmentPath ?? ''),
    createdDate: String(row.createdDate ?? row.CreatedDate ?? new Date().toISOString()),
    createdBy: Number(row.createdBy ?? row.CreatedBy ?? 0),
    branchId: Number(row.branchId ?? row.BranchId ?? 0),
  }
}

function mapSalesQuotationDetailLine(x: unknown): SalesQuotationDetailLine {
  const row = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>
  const del = row.isDeleted ?? row.IsDeleted
  const isDeleted =
    del === true || del === 'true' || del === 1 || del === '1' || String(del).toLowerCase() === 'true'
  return {
    id: Number(row.id ?? row.Id ?? 0),
    itemId: Number(row.itemId ?? row.ItemId ?? 0),
    batchId: Number(row.batchItemId ?? row.BatchItemId ?? row.batchId ?? row.BatchId ?? 0),
    quantity: Number(row.quantity ?? row.Quantity ?? 0),
    unitPrice: Number(row.unitPrice ?? row.UnitPrice ?? 0),
    discountPercent: Number(row.discountPercent ?? row.DiscountPercent ?? 0),
    taxRateId: Number(row.tax ?? row.Tax ?? row.taxRateId ?? row.TaxRateId ?? 0),
    isDeleted,
  }
}

/** List row from GetAllSalesQuotations. */
export type SalesQuotationListRow = {
  id: string
  /** Code column: `quotCode` from API when present, else `code`. */
  code: string
  reference: string
  warehouseId: number
  warehouseName: string
  orderDate: string
  estDeliveryDate: string
  total: number
  /** Raw `orderStatus` from API (see `QUOTATION_ORDER_STATUS_LABEL`). */
  orderStatus: number
  status: string
}

function quotationListDateOnly(v: unknown): string {
  const s = String(v ?? '')
  if (!s) return ''
  return s.includes('T') ? s.split('T')[0] : s.slice(0, 10)
}

const QUOTATION_ORDER_STATUS_LABEL: Record<number, string> = {
  0: 'draft',
  1: 'confirmed',
  2: 'processing',
  3: 'shipped',
  4: 'delivered',
  5: 'cancelled',
}

function formatQuotationOrderStatus(value: number): string {
  if (Number.isNaN(value)) return '-'
  return QUOTATION_ORDER_STATUS_LABEL[value] ?? String(value)
}

function mapSalesQuotationListRow(x: unknown): SalesQuotationListRow {
  const row = x as Record<string, unknown>
  const statusNum = Number(row?.orderStatus ?? row?.OrderStatus ?? 0)
  const id = Number(row?.id ?? row?.Id ?? 0)
  const quotCode = String(row?.quotCode ?? row?.QuotCode ?? '').trim()
  const codeOnly = String(row?.code ?? row?.Code ?? '').trim()
  const wid = Number(row?.warehouseId ?? row?.WarehouseId ?? 0)
  return {
    id: String(id),
    code: quotCode || codeOnly,
    reference: String(row?.reference ?? row?.Reference ?? ''),
    warehouseId: Number.isFinite(wid) ? wid : 0,
    warehouseName: String(row?.warehouseName ?? row?.WarehouseName ?? '').trim(),
    orderDate: quotationListDateOnly(row?.orderDate ?? row?.OrderDate),
    estDeliveryDate: quotationListDateOnly(row?.estDeliveryDate ?? row?.EstDeliveryDate),
    total: Number(row?.total ?? row?.Total ?? 0),
    orderStatus: Number.isFinite(statusNum) ? statusNum : 0,
    status: formatQuotationOrderStatus(statusNum),
  }
}

function extractQuotationListTotalCount(payload: unknown, itemsLength: number): number {
  const tryRead = (o: Record<string, unknown> | null | undefined): number | null => {
    if (!o || typeof o !== 'object') return null
    const keys = [
      'totalCount',
      'TotalCount',
      'totalRecords',
      'TotalRecords',
      'recordCount',
      'RecordCount',
      'count',
      'Count',
    ]
    for (const k of keys) {
      const n = Number(o[k])
      if (Number.isFinite(n) && n >= 0) return n
    }
    return null
  }
  const top = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null
  const fromTop = tryRead(top)
  if (fromTop !== null) return fromTop
  const inner = unwrap(payload)
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const fromInner = tryRead(inner as Record<string, unknown>)
    if (fromInner !== null) return fromInner
  }
  return itemsLength
}

export class SalesQuotationService {
  /** Resolve warehouse names via `/api/Warehouse/GetWarehouseById/{id}` when list only has an id. */
  private async enrichListRowsWarehouseNames(rows: SalesQuotationListRow[]): Promise<SalesQuotationListRow[]> {
    const ids = [...new Set(rows.map((r) => r.warehouseId).filter((id) => id > 0))]
    if (ids.length === 0) {
      return rows.map((r) => ({
        ...r,
        warehouseName: r.warehouseName || (r.warehouseId > 0 ? `Warehouse ${r.warehouseId}` : ''),
      }))
    }
    const nameById = new Map<number, string>()
    await Promise.all(
      ids.map(async (id) => {
        try {
          const w = await warehouseService.getWarehouseById(id)
          const label = [w.name, w.code].map((s) => String(s || '').trim()).find(Boolean) ?? ''
          if (label) nameById.set(id, label)
        } catch {
          /* keep list fallback */
        }
      }),
    )
    return rows.map((r) => {
      const resolved = r.warehouseId > 0 ? nameById.get(r.warehouseId) : undefined
      const name =
        (resolved && resolved.trim()) ||
        r.warehouseName.trim() ||
        (r.warehouseId > 0 ? `Warehouse ${r.warehouseId}` : '')
      return { ...r, warehouseName: name }
    })
  }

  /** GET GetSalesQuotationById/{id} */
  async getSalesQuotationById(id: number): Promise<SalesQuotationDetailMaster> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getSalesQuotationById(id),
    )
    let raw: unknown = unwrap(response.data)
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const o = raw as Record<string, unknown>
      const hasId = Number(o.id ?? o.Id ?? 0) > 0
      if (!hasId) {
        const inner = o.result ?? o.Result ?? o.data ?? o.Data
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          raw = inner
        }
      }
    }
    const row = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>
    return mapSalesQuotationDetailMaster(row)
  }

  /** GET GetSalesQuotationLinesById/{id} */
  async getSalesQuotationLinesById(id: number): Promise<SalesQuotationDetailLine[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getSalesQuotationLinesById(id),
    )
    const items = extractListPayload(response.data)
    return items.map(mapSalesQuotationDetailLine)
  }

  /** GET GetAllSalesQuotations?branchId=&pageSize=&pageNumber= */
  async getAllSalesQuotations(
    branchId: number,
    pageSize: number,
    pageNumber: number,
  ): Promise<{ rows: SalesQuotationListRow[]; totalCount: number }> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getAllSalesQuotations(branchId, pageSize, pageNumber),
    )
    const items = extractListPayload(response.data)
    const totalCount = extractQuotationListTotalCount(response.data, items.length)
    const rows = await this.enrichListRowsWarehouseNames(items.map(mapSalesQuotationListRow))
    return {
      rows,
      totalCount: Math.max(totalCount, rows.length),
    }
  }

  async getCustomers(branchId: number): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getAllCustomers(branchId),
    )
    const arr = toArray(response.data)
    return arr.map((x: unknown) => {
      const row = x as Record<string, unknown>
      return {
        id: Number(row?.customerId ?? row?.id ?? row?.Id ?? 0),
        name: String(
          row?.customerNameEn ?? row?.customerNameAr ?? row?.customerName ?? row?.name ?? row?.Name ?? '',
        ),
      }
    })
  }

  async getWarehouses(branchId: number): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getWarehouses(branchId),
    )
    const arr = toArray(response.data)
    return arr.map((x: unknown) => {
      const row = x as Record<string, unknown>
      return {
        id: Number(row?.id ?? row?.Id ?? row?.warehouseId ?? row?.WarehouseId ?? 0),
        name: String(row?.name ?? row?.warehouseName ?? row?.WarehouseName ?? row?.code ?? ''),
      }
    })
  }

  async getPaymentMethods(): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.salesQuotation.paymentMethods)
    const arr = toArray(response.data)
    return arr
      .filter((x) => {
        const row = x as Record<string, unknown>
        const active = row?.isActive ?? row?.IsActive
        if (active === undefined || active === null) return true
        return active === 1 || active === true || active === '1'
      })
      .map((x: unknown) => {
        const row = x as Record<string, unknown>
        return {
          id: Number(row?.id ?? row?.Id ?? row?.lookupId ?? 0),
          name: String(row?.lookupNameEn ?? row?.lookupNameAr ?? row?.name ?? row?.Name ?? ''),
        }
      })
  }

  async getPaymentTerms(): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.salesQuotation.paymentTerms)
    const arr = toArray(response.data)
    return arr.map((x: unknown) => {
      const row = x as Record<string, unknown>
      return {
        id: Number(row?.id ?? row?.Id ?? row?.lookupId ?? 0),
        name: String(
          row?.lookupNameEn ?? row?.lookupNameAr ?? row?.paymentTermNameEn ?? row?.paymentTermNameAr ?? row?.name ?? '',
        ),
      }
    })
  }

  async getAllItems(branchId: number): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getAllItems(branchId),
    )
    const arr = extractListPayload(response.data)
    return arr.map((x: unknown) => this.mapItemListRowToOption(x))
  }

  /** GET GetItemsByWarehouse/{warehouseId} — items available in that warehouse. */
  async getItemsByWarehouse(warehouseId: number): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getItemsByWarehouse(warehouseId),
    )
    const arr = extractListPayload(response.data)
    return arr.map((x: unknown) => this.mapItemListRowToOption(x)).filter((o) => o.id > 0)
  }

  private mapItemListRowToOption(x: unknown): SalesQuotationOption {
    const row = x as Record<string, unknown>
    const id = Number(row?.itemId ?? row?.id ?? row?.Id ?? 0)
    const code = String(row?.itemCode ?? row?.ItemCode ?? row?.code ?? row?.sku ?? row?.SKU ?? '').trim()
    const title = String(
      row?.itemNameEn ??
        row?.ItemNameEn ??
        row?.itemNameAr ??
        row?.ItemNameAr ??
        row?.itemName ??
        row?.name ??
        row?.Name ??
        '',
    ).trim()
    const name =
      code && title ? `${code} — ${title}` : title || code || (id > 0 ? `Item ${id}` : '')
    return { id, name }
  }

  async getAllTaxRates(): Promise<SalesQuotationTaxRate[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.salesQuotation.getAllTaxRates)
    const arr = extractListPayload(response.data)
    return arr.map((x: unknown) => {
      const row = x as Record<string, unknown>
      const id = Number(row?.taxRateId ?? row?.TaxRateId ?? row?.id ?? row?.Id ?? 0)
      const percent = Number(row?.taxPercent ?? row?.TaxPercent ?? 0)
      const nameEn = String(row?.taxRateNameEn ?? row?.TaxRateNameEn ?? '').trim()
      const nameAr = String(row?.taxRateNameAr ?? row?.TaxRateNameAr ?? '').trim()
      const def = row?.isDefault ?? row?.IsDefault
      const isDefault = def === true || def === 1 || def === '1' || String(def).toLowerCase() === 'true'
      return {
        id,
        nameEn: nameEn || nameAr || `Tax ${id}`,
        nameAr: nameAr || nameEn || `Tax ${id}`,
        percent,
        isDefault,
      }
    })
  }

  async getAllWarehouseItems(): Promise<SalesQuotationWarehouseStockRow[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getAllWarehouseItems,
    )
    const arr = extractListPayload(response.data)
    return arr.map((x: unknown) => {
      const row = x as Record<string, unknown>
      const wid =
        row?.warehouseId ??
        row?.WarehouseId ??
        row?.wareHouseId ??
        row?.WareHouseId ??
        row?.whId ??
        row?.WhId ??
        row?.warehouseID
      const iid = row?.itemId ?? row?.ItemId ?? row?.itemID ?? row?.ItemID ?? row?.stockItemId
      const bid = row?.batchItemId ?? row?.BatchItemId ?? row?.batchId ?? row?.BatchId
      return {
        id: Number(row?.id ?? row?.Id ?? 0),
        itemId: Number(iid ?? 0),
        batchItemId: Number(bid ?? 0),
        warehouseId: Number(wid ?? 0),
        availableQuantity: Number(row?.availableQuantity ?? row?.AvailableQuantity ?? row?.qty ?? row?.Qty ?? 0),
      }
    })
  }

  async getBatchesByWarehouseAndItem(warehouseId: number, itemId: number): Promise<SalesQuotationOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getBatchesByWarehouseAndItem(warehouseId, itemId),
    )
    const arr = extractListPayload(response.data)
    return arr
      .map((x: unknown) => {
        const row = x as Record<string, unknown>
        const id = Number(
          row?.batchItemId ??
            row?.BatchItemId ??
            row?.batchId ??
            row?.BatchId ??
            row?.id ??
            row?.Id ??
            0,
        )
        const name = String(
          row?.code ?? row?.batchCode ?? row?.BatchCode ?? row?.name ?? row?.Name ?? (id > 0 ? `Batch ${id}` : ''),
        ).trim()
        return { id, name: name || (id > 0 ? `Batch ${id}` : '') }
      })
      .filter((x) => x.id > 0)
  }

  async getItemDetails(warehouseId: number, itemId: number, batchId: number): Promise<SalesQuotationItemDetails> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.salesQuotation.getItemDetails(warehouseId, itemId, batchId),
    )
    const arr = extractListPayload(response.data)
    let raw: unknown = arr[0]
    if (raw === undefined || raw === null) {
      raw = unwrap(response.data)
    }
    const row = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>
    const availableQuantity = Number(
      row?.availableQuantity ??
        row?.AvailableQuantity ??
        row?.qty ??
        row?.Qty ??
        row?.quantity ??
        row?.Quantity ??
        row?.stockQty ??
        row?.balanceQty ??
        0,
    )
    const unitPrice = Number(
      row?.unitPrice ??
        row?.UnitPrice ??
        row?.price ??
        row?.Price ??
        row?.salePrice ??
        row?.SalePrice ??
        row?.sellingPrice ??
        row?.rate ??
        row?.Rate ??
        0,
    )
    return {
      availableQuantity: Number.isFinite(availableQuantity) ? availableQuantity : 0,
      unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
    }
  }

  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false')
      return
    }
    formData.append(key, String(value))
  }

  /** Swagger placeholder / bad data becomes the word "string" and breaks int/decimal/Guid parse on .NET. */
  private cleanTextField(value: unknown): string {
    if (value === undefined || value === null) return ''
    const s = String(value).trim()
    if (/^string$/i.test(s)) return ''
    return s
  }

  private roundMoney(n: unknown): number {
    const x = Number(n)
    if (!Number.isFinite(x)) return 0
    return Math.round(x * 100) / 100
  }

  private formInt(n: unknown): string {
    const x = Number(n)
    if (!Number.isFinite(x)) return '0'
    return String(Math.trunc(x))
  }

  private formMoney(n: unknown): string {
    return this.roundMoney(n).toFixed(2)
  }

  /** API date fields: yyyy-MM-dd only. */
  private formDateOnly(value: unknown): string {
    const s = this.cleanTextField(value)
    if (!s) return ''
    if (s.includes('T')) return s.slice(0, 10)
    return s.length >= 10 ? s.slice(0, 10) : s
  }

  /** Created/Modified — .NET-friendly local date-time string (no timezone suffix issues). */
  private formDateTimeDotNet(value: unknown): string {
    const raw = String(value ?? '').trim()
    const d = raw ? new Date(raw) : new Date()
    const date = Number.isNaN(d.getTime()) ? new Date() : d
    const p = (n: number) => String(n).padStart(2, '0')
    const ms = String(date.getMilliseconds()).padStart(3, '0')
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}.${ms}`
  }

  /**
   * Line object for `SalesQuotationLinesJson` — same conventions as `salesOrderService` lines:
   * numeric fields as numbers; `isDeleted` as strings `'true'` / `'false'` (API expects string, not JSON boolean).
   */
  private lineToWire(row: SalesQuotationSaveLine): Record<string, number | string> {
    const qty = Number(row.quantity)
    return {
      taxAmount: this.roundMoney(row.taxAmount),
      discountPercent: this.roundMoney(row.discountPercent),
      unitPrice: this.roundMoney(row.unitPrice),
      orderId: Math.trunc(Number(row.orderId) || 0),
      quantity: Number.isFinite(qty) ? this.roundMoney(qty) : 0,
      tax: Math.trunc(Number(row.tax) || 0),
      lineAmount: this.roundMoney(row.lineAmount),
      itemId: Math.trunc(Number(row.itemId) || 0),
      isDeleted: row.isDeleted ? 'true' : 'false',
      batchId: Math.trunc(Number(row.batchId) || 0),
      unitCostPrice: this.roundMoney(row.unitCostPrice),
      discountAmount: this.roundMoney(row.discountAmount),
      taxPercent: this.roundMoney(row.taxPercent),
      id: Math.trunc(Number(row.id) || 0),
      taxableAmount: this.roundMoney(row.taxableAmount),
    }
  }

  private serializeItemListForApi(lines: SalesQuotationSaveLine[]): string {
    return JSON.stringify(lines.map((row) => this.lineToWire(row)))
  }

  private buildQuotationSaveFormData(
    payload: SalesQuotationSavePayload,
    salesQuotationLinesJson: string,
  ): FormData {
    const formData = new FormData()
    this.appendFormField(formData, 'Id', this.formInt(payload.id))
    this.appendFormField(formData, 'QuotCode', this.cleanTextField(payload.quotCode))
    this.appendFormField(formData, 'Code', this.cleanTextField(payload.code))
    this.appendFormField(formData, 'CustomerName', this.cleanTextField(payload.customerName))
    this.appendFormField(formData, 'WarehouseName', this.cleanTextField(payload.warehouseName))
    this.appendFormField(formData, 'Reference', this.cleanTextField(payload.reference))
    this.appendFormField(formData, 'OrderDate', this.formDateOnly(payload.orderDate))
    this.appendFormField(formData, 'EstDeliveryDate', this.formDateOnly(payload.estDeliveryDate))
    this.appendFormField(formData, 'CustomerId', this.formInt(payload.customerId))
    this.appendFormField(formData, 'PaymentMethod', this.formInt(payload.paymentMethod))
    this.appendFormField(formData, 'TaxableAmount', this.formMoney(payload.taxableAmount))
    this.appendFormField(formData, 'TaxAmount', this.formMoney(payload.taxAmount))
    this.appendFormField(formData, 'Discount', this.formMoney(payload.discount))
    this.appendFormField(formData, 'Total', this.formMoney(payload.total))
    this.appendFormField(formData, 'SalsePersonId', this.formInt(payload.salesPersonId))
    this.appendFormField(formData, 'WarehouseId', this.formInt(payload.warehouseId))
    this.appendFormField(formData, 'PaymentTerm', this.formInt(payload.paymentTerm))
    this.appendFormField(formData, 'OrderStatus', this.formInt(payload.orderStatus))
    this.appendFormField(formData, 'AttachmentPath', this.cleanTextField(payload.attachmentPath))
    this.appendFormField(formData, 'CreatedDate', this.formDateTimeDotNet(payload.createdDate))
    this.appendFormField(formData, 'CreatedBy', this.formInt(payload.createdBy))
    this.appendFormField(formData, 'ModifiedDate', this.formDateTimeDotNet(payload.modifiedDate))
    this.appendFormField(formData, 'ModifiedBy', this.formInt(payload.modifiedBy))
    this.appendFormField(formData, 'BranchId', this.formInt(payload.branchId))
    /** Same as sales orders: lines only in JSON (no separate `itemList[n]` parts). */
    this.appendFormField(formData, 'SalesQuotationLinesJson', salesQuotationLinesJson)

    if (payload.attachmentFile) {
      formData.append('AttachmentFile', payload.attachmentFile)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }
    return formData
  }

  /** POST multipart/form-data — Save and Update use the same form fields as Swagger. */
  private async postQuotationSaveOrUpdate(
    url: string,
    action: 'Save' | 'Update',
    payload: SalesQuotationSavePayload,
  ): Promise<unknown> {
    const salesQuotationLinesJson = this.serializeItemListForApi(payload.itemList)
    const { attachmentFile, itemList, ...master } = payload
    const masterForLog = {
      ...master,
      orderDate: this.formDateOnly(master.orderDate),
      estDeliveryDate: this.formDateOnly(master.estDeliveryDate),
      createdDate: this.formDateTimeDotNet(master.createdDate),
      modifiedDate: this.formDateTimeDotNet(master.modifiedDate),
      quotCode: this.cleanTextField(master.quotCode),
      code: this.cleanTextField(master.code),
      customerName: this.cleanTextField(master.customerName),
      warehouseName: this.cleanTextField(master.warehouseName),
      reference: this.cleanTextField(master.reference),
      attachmentPath: this.cleanTextField(master.attachmentPath),
    }
    console.log(`[SalesQuotation] ${action} — multipart master (text fields)`, masterForLog)
    console.log(`[SalesQuotation] ${action} — SalesQuotationLinesJson`, salesQuotationLinesJson)
    console.log(`[SalesQuotation] ${action} — itemList (parsed)`, itemList)
    console.log(
      `[SalesQuotation] ${action} — AttachmentFile`,
      attachmentFile ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type } : null,
    )
    const formData = this.buildQuotationSaveFormData(payload, salesQuotationLinesJson)
    const response = await apiClient.post(url, formData)
    return response.data
  }

  async save(payload: SalesQuotationSavePayload): Promise<unknown> {
    return this.postQuotationSaveOrUpdate(endpoints.salesQuotation.save, 'Save', payload)
  }

  async update(payload: SalesQuotationSavePayload): Promise<unknown> {
    return this.postQuotationSaveOrUpdate(endpoints.salesQuotation.update, 'Update', payload)
  }

  /** ERP-style `{ result: { isError, msg } }` on HTTP 200. */
  private assertSalesQuotationResultOk(data: unknown, fallbackMsg: string): void {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return
    const o = data as Record<string, unknown>
    const r = o.result ?? o.Result
    if (r && typeof r === 'object' && !Array.isArray(r)) {
      const ro = r as Record<string, unknown>
      if (ro.isError === true) {
        const msg = typeof ro.msg === 'string' && ro.msg.trim() ? ro.msg.trim() : fallbackMsg
        const err = new Error(msg) as Error & { response?: { data: unknown } }
        err.response = { data }
        throw err
      }
    }
  }

  /**
   * POST ConvertToOrder/{id}. Some backends return HTTP 200 with `{ result: { isError: true, msg } }`.
   */
  async convertToOrder(id: number): Promise<unknown> {
    const response = await apiClient.post<unknown>(endpoints.salesQuotation.convertToOrder(id))
    const data = response.data
    this.assertSalesQuotationResultOk(data, 'Convert to order failed.')
    console.log('[SalesQuotation] ConvertToOrder — API response:', data)
    return data
  }

  /**
   * POST CombinedConvertToOrder — body is JSON array of quotation ids (Swagger: `application/json-patch+json`).
   */
  async combinedConvertToOrder(quotationIds: number[]): Promise<unknown> {
    const ids = quotationIds.filter((id) => Number.isFinite(id) && id > 0)
    const response = await apiClient.post<unknown>(endpoints.salesQuotation.combinedConvertToOrder, ids, {
      headers: { 'Content-Type': 'application/json-patch+json' },
    })
    const data = response.data
    this.assertSalesQuotationResultOk(data, 'Combined convert to order failed.')
    console.log('[SalesQuotation] CombinedConvertToOrder — API response:', data)
    return data
  }

  /** POST Delete/{id} */
  async deleteQuotation(id: number): Promise<unknown> {
    const response = await apiClient.post<unknown>(endpoints.salesQuotation.delete(id))
    const data = response.data
    this.assertSalesQuotationResultOk(data, 'Delete quotation failed.')
    console.log('[SalesQuotation] Delete — API response:', data)
    return data
  }
}

export const salesQuotationService = new SalesQuotationService()
